
import React, { useState, useEffect } from 'react';
import { Contact, X, ExternalLink, QrCode } from 'lucide-react';
import { siteConfig } from '../../config/site';
import { useLanguage } from '../../App';

const BusinessCard: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const qrImageUrl = `https://drive.google.com/thumbnail?id=${siteConfig.businessCardQrCode}&sz=w800`;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 sm:bottom-28 right-4 sm:right-8 z-[45] flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-secondary text-white border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.5)] transition-all duration-500 transform hover:scale-105 active:scale-95 group ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
        }`}
      >
        <div className="relative">
          <Contact size={18} className="sm:size-20 relative z-10 text-accent" />
          <div className="absolute inset-0 bg-accent blur-md opacity-0 group-hover:opacity-40 transition-opacity"></div>
        </div>
        <span className="font-bold text-[10px] sm:text-xs uppercase tracking-widest">{t.businessCard}</span>
      </button>

      {/* Modal Popup */}
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 transition-all duration-500 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/90 backdrop-blur-xl"
          onClick={() => setIsOpen(false)}
        />

        {/* Modal Content */}
        <div className={`relative bg-secondary border border-accent/20 max-w-sm w-full max-h-[90vh] overflow-y-auto scrollbar-hide p-6 sm:p-12 shadow-2xl transition-all duration-700 ${isOpen ? 'translate-y-0 scale-100' : 'translate-y-12 scale-95'}`}>
          <button 
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 text-white/40 hover:text-accent transition-colors p-2"
          >
            <X size={24} />
          </button>

          <div className="text-center pt-4 sm:pt-0">
            <div className="mb-4 sm:mb-6 flex justify-center">
                <img src={siteConfig.images.logo} alt="Santi" className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border border-accent/20 bg-black" />
            </div>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-white uppercase tracking-widest mb-1 sm:mb-2">{siteConfig.brandName}</h3>
            <p className="text-accent text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em] sm:tracking-[0.4em] mb-6 sm:mb-8">Digital Business Card</p>
            
            <div className="relative group mb-6 sm:mb-8 p-1 bg-white/5 border border-white/10 rounded-xl overflow-hidden shadow-2xl mx-auto max-w-[240px] sm:max-w-none">
              <img 
                src={qrImageUrl} 
                alt="QR Code" 
                className="w-full aspect-square object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                 {/* Fix: Moved responsive sizing to Tailwind classes to resolve 'sm:size' TS error */}
                 <QrCode className="text-accent w-9 h-9 sm:w-12 sm:h-12" />
              </div>
            </div>

            <div className="space-y-4">
              <a 
                href={siteConfig.businessCardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 sm:gap-3 w-full py-3 sm:py-4 bg-accent text-black font-black uppercase tracking-[0.1em] sm:tracking-[0.2em] text-[10px] sm:text-[11px] hover:bg-white transition-all shadow-xl"
              >
                {/* Fix: Moved responsive sizing to Tailwind classes to resolve 'sm:size' TS error */}
                Open Digital Card <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </a>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/30 hover:text-white transition-colors uppercase text-[8px] sm:text-[9px] font-black tracking-widest"
              >
                Close Portal
              </button>
            </div>
          </div>
          
          {/* Aesthetic Corners */}
          <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-t border-l border-accent/20 pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-b border-r border-accent/20 pointer-events-none"></div>
        </div>
      </div>
    </>
  );
};

export default BusinessCard;
